import React, { useState, useMemo } from "react"
import {
  useSchedulesSchedulesListQuery,
  useSchedulesBreaksByTeamMemberRetrieveQuery,
  useSchedulesSchedulesByTeamMemberRetrieveQuery,
  useSchedulesSchedulesBySalonRetrieveQuery,
  useAuthProfileRetrieveQuery,
} from "../../../../services/GLOWClient"
import DetailSchedule from "./detailSchedule"
import {
  Avatar,
  Button,
  Alert,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material"
import { Add, Edit, Delete, Coffee, AccessTime, MoreVert, Info } from "@mui/icons-material"
import Notification from "../../../common/Notification"
import dayUtils from "../../../../utils/dayUtils"
import { theme } from "../../../../theme"
import { PrimaryButton, TitleScreen } from "../../../../SystemDesign/styles"
import moment from "moment"
import "./schedule.css"
import AddSchedule from "./addSchedule"
import DeleteSchedule from "./deleteSchedule"
import UpdateSchedule from "./updateSchedule"
import AddBreak from "../breaks/AddBreak"
import { useAuth } from "../../../../context/AuthContext"
import { useTranslation } from "react-i18next"
import Can from "../../../../permissions/Can"
import { useAbility } from "../../../../permissions/AbilityContext.js"

const Schedule = ({ salonId: propSalonId }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const ability = useAbility()

  const [viewMode, setViewMode] = useState("calendar") // 'table' or 'calendar'
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)
  const [isAddBreakDialogOpen, setIsAddBreakDialogOpen] = useState(false)
  const [selectedBreakData, setSelectedBreakData] = useState(null)
  const [breaksDialogOpen, setBreaksDialogOpen] = useState(false)
  const [selectedTeamMember, setSelectedTeamMember] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuSchedule, setMenuSchedule] = useState(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)

  // États pour les notifications
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  })

  // Fonction pour afficher une notification
  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    })
  }

  // Fonction pour fermer la notification
  const closeNotification = () => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }))
  }

  // Get user profile data from API
  const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery()

  // Determine if the user is an employee
  const isEmployee = userProfile?.role === "professional_employe"

  // Get team member ID from the authenticated user object
  const teamMemberId = useMemo(() => {
    return userProfile?.team_member_id
  }, [userProfile?.team_member_id])

  // For regular users and professional responsables, use the general schedules list endpoint
  // with owner filter for professional responsables
  const {
    data: allSchedules,
    error: schedulesError,
    isLoading: allSchedulesLoading,
    refetch: refetchSchedules,
  } = useSchedulesSchedulesListQuery(
    userProfile?.role === 'professional_responsable' ? { owner: user?.id } : undefined, 
    { 
      skip: isEmployee || profileLoading,
      refetchOnMountOrArgChange: true,
    }
  )

  // When a specific salonId is provided, prefer fetching schedules by salon
  const {
    data: salonSchedules,
    error: salonSchedulesError,
    isLoading: salonSchedulesLoading,
    refetch: refetchSalonSchedules,
  } = useSchedulesSchedulesBySalonRetrieveQuery(
    { salonId: Number(propSalonId) },
    {
      skip: isEmployee || !propSalonId || profileLoading,
      refetchOnMountOrArgChange: true,
    },
  )

  // For employee users, use the specific team member schedules endpoint
  const {
    data: employeeSchedules,
    error: employeeSchedulesError,
    isLoading: employeeSchedulesLoading,
    refetch: refetchEmployeeSchedules,
  } = useSchedulesSchedulesByTeamMemberRetrieveQuery(
    { teamMemberId: Number.parseInt(teamMemberId, 10) }, // Ensure it's a number
    {
      skip: !isEmployee || !teamMemberId || profileLoading,
      // Force refetch when team member ID changes
      refetchOnMountOrArgChange: true,
    },
  )

  // For employee users, also fetch their breaks
  const {
    data: employeeBreaks,
    error: employeeBreaksError,
    isLoading: employeeBreaksLoading,
    refetch: refetchEmployeeBreaks,
  } = useSchedulesBreaksByTeamMemberRetrieveQuery(
    teamMemberId ? { teamMemberId: Number.parseInt(teamMemberId, 10) } : undefined,
    {
      skip: !isEmployee || !teamMemberId || profileLoading,
      // Force refetch when team member ID changes
      refetchOnMountOrArgChange: true,
    },
  )

  // Combine the results based on which endpoint was used
  // Priority: Employee-specific -> By-salon (when salonId provided) -> General list
  const schedules = isEmployee
    ? employeeSchedules
    : (propSalonId ? salonSchedules : allSchedules)
  const error = isEmployee
    ? employeeSchedulesError
    : (propSalonId ? salonSchedulesError : schedulesError)
  // Avoid variable redeclaration
  const loadingState = isEmployee
    ? employeeSchedulesLoading
    : (propSalonId ? salonSchedulesLoading : allSchedulesLoading)
  const refetch = isEmployee
    ? refetchEmployeeSchedules
    : (propSalonId ? refetchSalonSchedules : refetchSchedules)

  // Combined loading state
  const isLoading = profileLoading || loadingState

  // Days of the week for display in French with mapping
  const daysOfWeek = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"]

  const getLocalizedDayLabel = (day) => {
    const normalizedDay = dayUtils.normalizeDayName(day)
    const dayKey = normalizedDay === "sun" ? "sunday" : normalizedDay
    return t(`pro_team_member.add_schedule.weekdays_full.${dayKey}`)
  }

  // Create a mapping for day names (handle different possible formats)
  const dayMapping = {
    lundi: "Lundi",
    mardi: "Mardi",
    mercredi: "Mercredi",
    jeudi: "Jeudi",
    vendredi: "Vendredi",
    samedi: "Samedi",
    dimanche: "Dimanche",
    monday: "Lundi",
    tuesday: "Mardi",
    wednesday: "Mercredi",
    thursday: "Jeudi",
    friday: "Vendredi",
    saturday: "Samedi",
    sunday: "Dimanche",
  }

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return ""
    return moment(timeString, "HH:mm:ss").format("HH:mm")
  }

  // Format hour for display
  const formatHour = (hour) => {
    if (hour === 24) return "00:00"
    return `${hour.toString().padStart(2, "0")}:00`
  }

  // Handle actions for specific schedules
  const handleEdit = (schedule) => {
    if (!schedule) {
      console.error("No schedule provided for editing")
      return
    }
    setSelectedSchedule(schedule)
    setIsUpdateDialogOpen(true)
  }

  const handleDelete = (schedule) => {
    if (!schedule) {
      console.error("No schedule provided for deletion")
      return
    }
    setSelectedSchedule(schedule)
    setIsDeleteDialogOpen(true)
  }

  // Handle menu actions
  const handleMenuClick = (event, schedule) => {
    setAnchorEl(event.currentTarget)
    setMenuSchedule(schedule)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuSchedule(null)
  }

  const handleMenuAction = (action) => {
    if (menuSchedule) {
      switch (action) {
        case 'info':
          setSelectedSchedule(menuSchedule)
          setShowDetailDialog(true)
          break
        case 'edit':
          handleEdit(menuSchedule)
          break
        case 'delete':
          handleDelete(menuSchedule)
          break
      }
    }
    handleMenuClose()
  }

  // Handle actions for table view (legacy support)
  const handleEditRow = (row) => {
    // Find the first schedule in the row to edit
    const firstScheduleDay = daysOfWeek.find((day) => row[`${day}_schedules`]?.length > 0)
    if (firstScheduleDay && row[`${firstScheduleDay}_schedules`]?.[0]) {
      handleEdit(row[`${firstScheduleDay}_schedules`][0])
    } else {
      console.error("No schedule found to edit")
    }
  }

  const handleDeleteRow = (row) => {
    // Find the first schedule in the row to delete
    const firstScheduleDay = daysOfWeek.find((day) => row[`${day}_schedules`]?.length > 0)
    if (firstScheduleDay && row[`${firstScheduleDay}_schedules`]?.[0]) {
      handleDelete(row[`${firstScheduleDay}_schedules`][0])
    } else {
      console.error("No schedule found to delete")
    }
  }

  // Debug schedules data

  // Use the dayUtils normalization functions for consistent day handling
  const normalizeDayName = dayUtils.normalizeDayName
  const getDisplayDay = dayUtils.getDisplayDay

  // Prepare data for the table view - group by team member
  const tableData = useMemo(() => {
    if (!schedules || !Array.isArray(schedules)) return []

    // Group schedules by team member
    const memberSchedules = {}

    schedules.forEach((schedule) => {
      const memberId = schedule.team_member?.team_member_id
      const memberName = schedule.team_member?.user_details?.full_name || "Sans nom"
      const salonName = schedule.salon?.name || ""
      const day = schedule.day_of_week || ""
      // Use the dayUtils to get the normalized day name
      const normalizedDay = normalizeDayName(day)
      // Map to French display name for the calendar
      const dayKey = getDisplayDay(normalizedDay)

      // Create entry for this member if it doesn't exist
      if (!memberSchedules[memberId]) {
        memberSchedules[memberId] = {
          id: memberId,
          team_member: memberName,
          salon: salonName,
          team_member_data: schedule.team_member,
          salon_data: schedule.salon,
          // Initialize all days as strings for display and arrays for schedules
          ...Object.fromEntries(
            daysOfWeek.map((dayName) => [
              dayName,
              "", // Display string
            ]),
          ),
          ...Object.fromEntries(
            daysOfWeek.map((dayName) => [
              `${dayName}_schedules`,
              [], // Schedule arrays
            ]),
          ),
        }
      }

      // Add this schedule to the appropriate day
      if (dayKey && memberSchedules[memberId].hasOwnProperty(dayKey)) {
        const timeString = `${formatTime(schedule.start_time)} - ${formatTime(schedule.end_time)}`

        // Add to display string
        if (memberSchedules[memberId][dayKey]) {
          memberSchedules[memberId][dayKey] += `, ${timeString}`
        } else {
          memberSchedules[memberId][dayKey] = timeString
        }

        // Add to schedules array
        memberSchedules[memberId][`${dayKey}_schedules`].push(schedule)
      }
    })

    // Convert the object to an array
    return Object.values(memberSchedules)
  }, [schedules])

  // Table columns configuration for ReactVirtualizedTable with days as separate columns
  const columns = useMemo(
    () => [
      { width: 150, label: "Membre d'équipe", dataKey: "team_member" },
      { width: 150, label: "Salon", dataKey: "salon" },
      // Days of week as separate columns
      ...daysOfWeek.map((day) => ({
        width: 120,
        label: day.substring(0, 3), // Short day name (e.g., "Lun")
        dataKey: day,
        render: (cellData) => {
          // cellData should now be a string, not an object
          return cellData || "-"
        },
      })),
    ],
    [],
  )

  // Get unique team members for calendar view
  const teamMembers = useMemo(() => {
    if (!schedules || !Array.isArray(schedules)) return []

    // For employee view, we only need to show their own schedule
    if (isEmployee && teamMemberId) {
      // Create a single entry for the employee
      const firstSchedule = schedules.length > 0 ? schedules[0] : null

      if (firstSchedule && firstSchedule.team_member) {
        const memberName =
          firstSchedule.team_member.user_details?.full_name ||
          userProfile?.first_name + " " + userProfile?.last_name ||
          "Unknown"
        return [
          {
            id: teamMemberId,
            name: memberName,
            data: firstSchedule.team_member,
          },
        ]
      } else if (userProfile) {
        // If no schedule but we have user profile
        return [
          {
            id: teamMemberId,
            name: userProfile.first_name + " " + userProfile.last_name || "Unknown",
            data: { team_member_id: teamMemberId, user_details: userProfile },
          },
        ]
      }

      return []
    }

    // For admin view, show all team members
    const uniqueMembers = new Map()
    schedules.forEach((schedule) => {
      if (schedule.team_member && schedule.team_member.user_details) {
        const memberId = schedule.team_member.team_member_id || schedule.team_member.id
        const memberName = schedule.team_member.user_details.full_name || "Unknown"
        uniqueMembers.set(memberId, {
          id: memberId,
          name: memberName,
          data: schedule.team_member,
        })
      }
    })

    return Array.from(uniqueMembers.values())
  }, [schedules, isEmployee, teamMemberId, userProfile])

  // Group schedules by team member and day
  const schedulesByMemberAndDay = useMemo(() => {
    const grouped = {}

    if (schedules && Array.isArray(schedules)) {
      schedules.forEach((schedule) => {
        if (schedule.team_member && schedule.day_of_week) {
          const memberId = schedule.team_member.team_member_id || schedule.team_member.id
          const normalizedDay = normalizeDayName(schedule.day_of_week)

          if (!grouped[memberId]) {
            grouped[memberId] = {}
          }

          if (!grouped[memberId][normalizedDay]) {
            grouped[memberId][normalizedDay] = []
          }

          grouped[memberId][normalizedDay].push(schedule)
        }
      })
    }

    return grouped
  }, [schedules])

  // Group breaks by team member and day
  const breaksByMemberAndDay = useMemo(() => {
    const grouped = {}

    if (employeeBreaks && Array.isArray(employeeBreaks)) {
      employeeBreaks.forEach((breakItem) => {
        if (breakItem.team_member && breakItem.day_of_week) {
          const memberId = breakItem.team_member.team_member_id || breakItem.team_member.id
          const normalizedDay = normalizeDayName(breakItem.day_of_week)

          if (!grouped[memberId]) {
            grouped[memberId] = {}
          }

          if (!grouped[memberId][normalizedDay]) {
            grouped[memberId][normalizedDay] = []
          }

          grouped[memberId][normalizedDay].push(breakItem)
        }
      })
    }

    return grouped
  }, [employeeBreaks])

  // Get schedules for a team member on a specific day
  const getSchedulesForMemberDay = (memberId, day) => {
    const memberSchedules = schedulesByMemberAndDay[memberId]
    if (!memberSchedules) return []

    // Try to find schedules using the display day name
    if (memberSchedules[day]) {
      return memberSchedules[day]
    }

    // If not found, try to match using normalized day names
    for (const scheduleDay in memberSchedules) {
      if (dayUtils.isSameDay(scheduleDay, day)) {
        return memberSchedules[scheduleDay]
      }
    }

    return []
  }

  // Get breaks for a team member on a specific day
  const getBreaksForMemberDay = (memberId, day) => {
    // For employees, use the directly fetched breaks data
    if (isEmployee && employeeBreaks) {
      const breaksList = Array.isArray(employeeBreaks) ? employeeBreaks : [employeeBreaks]
      return breaksList.filter((breakItem) => {
        const bMemberId =
          breakItem?.team_member?.team_member_id ||
          breakItem?.team_member?.id ||
          breakItem?.team_member_id
        return bMemberId === memberId && dayUtils.isSameDay(breakItem.day_of_week, day)
      })
    }

    // For non-employees or fallback, use the grouped breaks data
    const memberBreaks = breaksByMemberAndDay[memberId]
    if (!memberBreaks) return []

    // Try to find breaks using the display day name
    if (memberBreaks[day]) {
      return memberBreaks[day]
    }

    // If not found, try to match using normalized day names
    for (const breakDay in memberBreaks) {
      if (dayUtils.isSameDay(breakDay, day)) {
        return memberBreaks[breakDay]
      }
    }

    return []
  }

  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"))

  // Calendar styles
  const calendarStyles = {
    container: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      overflow: "auto",
      maxHeight: isMobile ? "600px" : "800px", // Reduced height on mobile
    },
    header: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "200px repeat(7, minmax(120px, 1fr))",
      gap: "0",
      marginBottom: "0",
      backgroundColor: "#f5f5f5",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    memberColumn: {
      padding: isMobile ? "8px 4px" : "12px 8px", // Reduced padding on mobile
      backgroundColor: "#f9f9f9",
      fontWeight: "bold",
      textAlign: "center",
      borderRight: "1px solid #e0e0e0",
      fontSize: isMobile ? "0.9rem" : "1rem", // Smaller font on mobile
      width: "200px", // Fixed width
      minWidth: "200px",
      maxWidth: "200px",
    },
    dayHeader: {
      padding: isMobile ? "8px 4px" : "12px 8px", // Reduced padding on mobile
      backgroundColor: "#f0f0f0",
      fontWeight: "bold",
      textAlign: "center",
      borderRight: "1px solid #e0e0e0",
      fontSize: isMobile ? "0.8rem" : "1rem", // Smaller font on mobile
      minWidth: "120px", // Fixed minimum width
      flex: 1,
    },
    memberRow: {
      display: isMobile ? "block" : "grid", // Stack on mobile instead of grid
      gridTemplateColumns: isMobile ? "none" : "200px repeat(7, minmax(120px, 1fr))",
      gap: "0",
      backgroundColor: "#f5f5f5",
      borderBottom: "1px solid #e0e0e0",
      marginBottom: isMobile ? "16px" : "0", // Add spacing between members on mobile
    },
    memberNameCell: {
      padding: isMobile ? "12px 8px" : "8px",
      backgroundColor: "#fff",
      fontWeight: "medium",
      borderRight: isMobile ? "none" : "1px solid #e0e0e0",
      borderBottom: isMobile ? "1px solid #e0e0e0" : "none", // Bottom border on mobile
      display: "flex",
      alignItems: "center",
      fontSize: isMobile ? "14px" : "13px",
      justifyContent: isMobile ? "center" : "flex-start", // Center on mobile
      width: "200px", // Fixed width to match header
      minWidth: "200px",
      maxWidth: "200px",
    },
    dayCell: {
      backgroundColor: "#fff",
      borderRight: isMobile ? "none" : "1px solid #e0e0e0",
      borderBottom: isMobile ? "1px solid #e0e0e0" : "none", // Bottom border on mobile
      padding: isMobile ? "12px 8px" : "8px",
      minHeight: isMobile ? "80px" : "60px", // Taller cells on mobile for better touch targets
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minWidth: "120px", // Fixed minimum width to match header
      flex: 1,
    },
    scheduleBlock: {
      backgroundColor: theme.colors.primary?.light || "#e3f2fd",
      color: theme.colors.primary?.dark || "#1976d2",
      padding: isMobile ? "4px 6px" : "4px 8px", // Reduced padding on mobile
      borderRadius: "4px",
      margin: "2px 0",
      fontSize: isMobile ? "0.75rem" : "0.8rem", // Smaller font on mobile
      display: "flex",
      alignItems: "center",
      gap: "4px",
      flexWrap: isMobile ? "wrap" : "nowrap", // Allow wrapping on mobile
    },
    scheduleActions: {
      display: "flex",
      gap: isMobile ? "8px" : "4px", // More spacing between buttons on mobile
      marginLeft: "auto",
      flexShrink: 0,
    },
    actionButton: {
      minWidth: "unset",
      padding: isMobile ? "8px" : "2px", // Larger touch targets on mobile
      fontSize: "0.7rem",
      minHeight: isMobile ? "32px" : "auto", // Minimum height for touch targets
      minWidth: isMobile ? "32px" : "unset",
    },
    mobileDaySection: {
      backgroundColor: "#fff",
      borderRadius: "8px",
      marginBottom: "0",
      border: "none",
      boxShadow: "none",
    },
    mobileDayHeader: {
      backgroundColor: "#f0f0f0",
      padding: "12px 16px",
      fontWeight: "bold",
      fontSize: "1rem",
      borderBottom: "1px solid #e0e0e0",
    },
  }

  return (
    <div style={{ padding: isMobile ? "8px" : "24px" }}>
      {" "}
      {/* Reduced padding on mobile */}
      {/* Header Section with proper spacing */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "center" : "flex-start", // Center align on mobile
          marginBottom: isMobile ? "12px" : "32px", // Reduced margin on mobile
          gap: "8px",
          flexWrap: "wrap",
          flexDirection: isMobile ? "column" : "row", // Stack vertically on mobile
        }}
      >
        <Typography
          sx={{
            ...TitleScreen(theme.colors),
            fontSize: isMobile ? "1.5rem" : "2rem", // Smaller title on mobile
            textAlign: isMobile ? "center" : "left",
          }}
        >
          {t("pro_dashboard.salon.manage_schedules")}
        </Typography>
        <Can I="create" a="schedules">
          <Button
            onClick={() => {
              setIsAddDialogOpen(true);
            }}
            style={{
              ...PrimaryButton,
              backgroundColor: theme.colors.secondary[500],
              fontSize: isMobile ? "16px" : "14px", // Larger font on mobile
              padding: isMobile ? "12px 24px" : "8px 20px", // Larger padding on mobile
              height: isMobile ? "48px" : "40px", // Taller button on mobile
              borderRadius: "20px",
              whiteSpace: "nowrap",
              minWidth: isMobile ? "160px" : "140px", // Wider button on mobile
              textTransform: "none",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              fontWeight: "500",
              transition: "all 0.2s ease-in-out",
              width: isMobile ? "100%" : "auto", // Full width on mobile
            }}
            startIcon={<Add />}
          >
            {t("pro_team_member.add_schedule.add_new_schedule_button")}
          </Button>
        </Can>
      </div>
      {/* Toggle buttons for view mode */}
      {/* <div style={{ marginBottom: '16px' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newValue) => newValue && setViewMode(newValue)}
          aria-label="mode d'affichage"
          size="small"
          sx={{ backgroundColor: theme.colors.background.paper }}
        >
          <ToggleButton value="table" aria-label="vue tableau">
            <ViewList sx={{ mr: 1 }} /> Tableau
          </ToggleButton>
          <ToggleButton value="calendar" aria-label="vue calendrier">
            <CalendarMonth sx={{ mr: 1 }} /> Calendrier
          </ToggleButton>
        </ToggleButtonGroup>
      </div> */}
      {/* Content Section */}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "60px 0",
            color: theme.colors.text.secondary,
            flexDirection: isMobile ? "column" : "row", // Stack loading elements on mobile
            gap: isMobile ? 2 : 0,
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: isMobile ? 0 : 2, mt: isMobile ? 2 : 0 }}>
            {t("pro_team_member.add_schedule.loading_page")}
          </Typography>
        </Box>
      ) : error ? (
        <div
          style={{
            padding: isMobile ? "16px" : "20px", // Reduced padding on mobile
            backgroundColor: theme.colors.error?.light || "#ffebee",
            color: theme.colors.error?.main || "#d32f2f",
            borderRadius: "8px",
            border: `1px solid ${theme.colors.error?.main || "#d32f2f"}`,
            marginBottom: "24px",
            fontSize: isMobile ? "14px" : "16px", // Smaller font on mobile
          }}
        >
          {t("pro_team_member.add_schedule.load_error", { message: error?.message || "" })}
        </div>
      ) : !schedules || schedules.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("pro_dashboard.salon.no_schedules")}
        </Alert>
      ) : (
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden",
          }}
        >
          {isMobile ? (
            <div style={{ padding: "0", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px" }}>
              {daysOfWeek.map((day) => (
                <div key={day} style={{ ...calendarStyles.mobileDaySection, margin: 0 }}>
                  <div style={{ ...calendarStyles.mobileDayHeader, padding: "8px 12px", fontSize: "0.9rem" }}>{getLocalizedDayLabel(day)}</div>
                  <div style={{ padding: "8px" }}>
                    {teamMembers.map((member) => {
                      const memberSchedules = getSchedulesForMemberDay(member.id, day)
                      const breaks = isEmployee ? getBreaksForMemberDay(member.id, day) : []

                      if (memberSchedules.length === 0 && breaks.length === 0) {
                        return null // Don't show empty entries on mobile
                      }

                      return (
                        <div key={`${member.id}-${day}`} style={{ marginBottom: "8px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "4px",
                              padding: "4px 6px",
                              backgroundColor: "#f9f9f9",
                              borderRadius: "4px",
                            }}
                          >
                            <Avatar
                              src={member.avatar || undefined}
                              alt={member.name}
                              sx={{ width: 24, height: 24, mr: 1, fontSize: "0.75rem" }}
                            >
                              {member.name.charAt(0)}
                            </Avatar>
                            <Typography variant="caption" fontWeight="medium" sx={{ fontSize: "0.75rem" }}>
                              {member.name}
                            </Typography>
                          </div>

                          {/* Show schedules */}
                          {memberSchedules.map((schedule, idx) => (
                            <div key={`schedule-${schedule.schedule_id || idx}`} style={calendarStyles.scheduleBlock}>
                              <AccessTime fontSize="small" />
                              <span style={{ flex: 1 }}>
                                {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                              </span>

                              <div style={calendarStyles.scheduleActions}>
                                <Tooltip title={t("pro_dashboard.common.actions")}>
                                  <IconButton
                                    size="small"
                                    style={calendarStyles.actionButton}
                                    onClick={(e) => handleMenuClick(e, schedule)}
                                  >
                                    <MoreVert fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </div>
                          ))}

                          {/* Show breaks for employees */}
                          {isEmployee &&
                            breaks.map((breakItem, idx) => (
                              <div
                                key={`break-${breakItem.break_id || idx}`}
                                style={{
                                  ...calendarStyles.scheduleBlock,
                                  backgroundColor: theme.colors.secondary?.light || "#fff3e0",
                                  color: theme.colors.secondary?.dark || "#e65100",
                                }}
                              >
                                <Coffee fontSize="small" />
                                <span style={{ flex: 1 }}>
                                  {formatTime(breakItem.start_time)} - {formatTime(breakItem.end_time)}
                                </span>
                                <Typography variant="caption" sx={{ ml: 1 }}>
                                  {breakItem.description || t("pro_team_member.add_schedule.break")}
                                </Typography>
                              </div>
                            ))}
                        </div>
                      )
                    })}
                    {teamMembers.every(
                      (member) =>
                        getSchedulesForMemberDay(member.id, day).length === 0 &&
                        (!isEmployee || getBreaksForMemberDay(member.id, day).length === 0),
                    ) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {t("pro_team_member.add_schedule.no_schedule_for_day")}
                      </Typography>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Desktop layout - original grid layout
            <div style={calendarStyles.container}>
              {/* Calendar Header */}
              <div style={calendarStyles.header}>
                <div style={calendarStyles.memberColumn}>{t("pro_dashboard.team.employee")}</div>
                {daysOfWeek.map((day) => (
                  <div key={day} style={calendarStyles.dayHeader}>
                    {getLocalizedDayLabel(day)}
                  </div>
                ))}
              </div>

              {/* Calendar Body */}
              <div>
                {teamMembers.map((member) => (
                  <div key={member.id} style={calendarStyles.memberRow}>
                    <div style={calendarStyles.memberNameCell}>
                      <Avatar
                        src={member.avatar || undefined}
                        alt={member.name}
                        sx={{ width: 24, height: 24, mr: 1, fontSize: "0.8rem" }}
                      >
                        {member.name.charAt(0)}
                      </Avatar>
                      <span>{member.name}</span>
                    </div>

                    {/* Day Cells */}
                    {daysOfWeek.map((day) => {
                      const memberSchedules = getSchedulesForMemberDay(member.id, day)
                      const breaks = isEmployee ? getBreaksForMemberDay(member.id, day) : []

                      return (
                        <div key={`${member.id}-${day}`} style={calendarStyles.dayCell}>
                          {/* Show schedules */}
                          {memberSchedules.length > 0 ? (
                            memberSchedules.map((schedule, idx) => (
                              <Tooltip key={`schedule-${schedule.schedule_id || idx}`}>
                                <div style={calendarStyles.scheduleBlock}>
                                  <AccessTime fontSize="small" />
                                  {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                                  <div style={calendarStyles.scheduleActions}>
                                    <Tooltip title={t("pro_dashboard.common.actions")}>
                                      <IconButton
                                        size="small"
                                        style={calendarStyles.actionButton}
                                        onClick={(e) => handleMenuClick(e, schedule)}
                                      >
                                        <MoreVert fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                </div>
                              </Tooltip>
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {t("pro_dashboard.salon.no_schedules")}
                            </Typography>
                          )}

                          {/* Show breaks for employees */}
                          {isEmployee &&
                            breaks.length > 0 &&
                            breaks.map((breakItem, idx) => (
                              <div
                                key={`break-${breakItem.break_id || idx}`}
                                style={{
                                  ...calendarStyles.scheduleBlock,
                                  backgroundColor: theme.colors.secondary?.light || "#fff3e0",
                                  color: theme.colors.secondary?.dark || "#e65100",
                                }}
                              >
                                <Coffee fontSize="small" />
                                {formatTime(breakItem.start_time)} - {formatTime(breakItem.end_time)}
                                <Typography variant="caption" sx={{ ml: 1 }}>
                                  {breakItem.description || t("pro_team_member.add_schedule.break")}
                                </Typography>
                              </div>
                            ))}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Add Schedule Dialog */}
      <AddSchedule 
        open={isAddDialogOpen} 
        onClose={() => setIsAddDialogOpen(false)} 
        selectedSalonId={propSalonId}
        onSuccess={() => {
          // Refetch all relevant data based on user type and context
          if (isEmployee) {
            refetchEmployeeSchedules();
            refetchEmployeeBreaks();
          } else if (propSalonId) {
            refetchSalonSchedules();
          } else {
            refetchSchedules();
          }
          setIsAddDialogOpen(false);
          showNotification(t("pro_team_member.add_schedule.schedule_created_success"), 'success');
        }} 
      />
      {/* Update Schedule Dialog */}
      <UpdateSchedule
        open={isUpdateDialogOpen}
        onClose={() => setIsUpdateDialogOpen(false)}
        onSuccess={() => {
          // Refetch all relevant data based on user type and context
          if (isEmployee) {
            refetchEmployeeSchedules();
            refetchEmployeeBreaks();
          } else if (propSalonId) {
            refetchSalonSchedules();
          } else {
            refetchSchedules();
          }
          setIsUpdateDialogOpen(false);
          showNotification(t("pro_dashboard.salon.schedule_updated"), 'success');
        }}
        schedule={selectedSchedule}
      />
      {/* Delete Schedule Dialog */}
      <DeleteSchedule
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onSuccess={() => {
          // Refetch all relevant data based on user type and context
          if (isEmployee) {
            refetchEmployeeSchedules();
            refetchEmployeeBreaks();
          } else if (propSalonId) {
            refetchSalonSchedules();
          } else {
            refetchSchedules();
          }
          setIsDeleteDialogOpen(false);
          setSelectedSchedule(null);
          showNotification(t("pro_team_member.add_schedule.schedule_deleted_success"), 'success');
        }}
        schedule={selectedSchedule}
      />
      {/* Add Break Dialog */}
      {selectedBreakData && (
        <AddBreak
          open={isAddBreakDialogOpen}
          onClose={() => setIsAddBreakDialogOpen(false)}
          onSuccess={() => {
            // Fermer le dialogue et rafraîchir les données
            setIsAddBreakDialogOpen(false)
            
            // Refetch all relevant data based on user type and context
            if (isEmployee) {
              refetchEmployeeSchedules();
              refetchEmployeeBreaks();
            } else if (propSalonId) {
              refetchSalonSchedules();
            } else {
              refetchSchedules();
            }

            // Si le dialogue des pauses est ouvert, forcer son rafraîchissement
            if (breaksDialogOpen && selectedTeamMember) {
              // Créer une copie de l'objet pour forcer le rafraîchissement du composant
              setSelectedTeamMember({ ...selectedTeamMember })
            }
            
            showNotification(t("pro_team_member.add_schedule.break_added_success"), 'success');
          }}
          schedule={selectedSchedule}
          teamMember={selectedBreakData.teamMember}
          salon={selectedBreakData.salon}
          dayOfWeek={selectedBreakData.dayOfWeek}
        />
      )}
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            minWidth: '150px'
          }
        }}
      >
        <MenuItem onClick={() => handleMenuAction('info')} sx={{ py: 1 }}>
          <Info fontSize="small" sx={{ mr: 1, color: theme.colors.primary[500] }} />
          {t("pro_team_member.add_schedule.details")}
        </MenuItem>
        <Can I="update" a="schedules">
          {!isEmployee && (
            <MenuItem onClick={() => handleMenuAction('edit')} sx={{ py: 1 }}>
              <Edit fontSize="small" sx={{ mr: 1, color: theme.colors.secondary[500] }} />
              {t("pro_dashboard.common.edit")}
            </MenuItem>
          )}
        </Can>
        <Can I="delete" a="schedules">
          <MenuItem onClick={() => handleMenuAction('delete')} sx={{ py: 1 }}>
            <Delete fontSize="small" sx={{ mr: 1, color: theme.colors.error[500] }} />
            {t("pro_dashboard.common.delete")}
          </MenuItem>
        </Can>
      </Menu>

      {/* Detail Schedule Dialog - triggered from menu */}
      {selectedSchedule && showDetailDialog && (
        <DetailSchedule
          schedule={selectedSchedule}
          breaks={getBreaksForMemberDay(
            selectedSchedule.team_member?.team_member_id || selectedSchedule.team_member?.id,
            selectedSchedule.day_of_week
          )}
          open={showDetailDialog}
          onClose={() => setShowDetailDialog(false)}
        />
      )}

      {/* Notification Component */}
      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={closeNotification}
      />
    </div>
  )
}

export default Schedule
